import React, { useContext, useMemo, useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useIsTouch } from '@/hooks/use-touch';
import { useIsMobile } from '@/hooks/use-mobile';
import NumericKeyboard from '@/components/NumericKeyboard';
import { cn } from '@/lib/utils';
import { SheetPortal } from '@/components/ui/sheet';
import {
  formatWeight,
  initialWeightInput,
  parseWeightInput,
  type WeightInputUnit,
} from '@/utils/weightPricing';

interface TabItemQuantityDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentQuantity: number;
  productName: string;
  onConfirm: (newQuantity: number) => void;
  title?: string;
  confirmLabel?: string;
  minQuantity?: number;
  maxQuantity?: number;
  isConfirmDisabled?: boolean;
  children?: React.ReactNode;
  isSoldByWeight?: boolean;
}

const TabItemQuantityDialog: React.FC<TabItemQuantityDialogProps> = ({
  isOpen,
  onClose,
  currentQuantity,
  productName,
  onConfirm,
  title,
  confirmLabel = 'Global.save',
  minQuantity = 0,
  maxQuantity = Infinity,
  isConfirmDisabled = false,
  children,
  isSoldByWeight = false,
}) => {
  const { translator } = useContext(LanguageProviderContext);
  const isTouch = useIsTouch();
  const isMobile = useIsMobile();
  const useCustomNumpad = isTouch && !isMobile;

  const initialWeight = initialWeightInput(currentQuantity);
  const [quantity, setQuantity] = useState(
    isSoldByWeight ? initialWeight.value : currentQuantity.toString()
  );
  const [unit, setUnit] = useState<WeightInputUnit>(initialWeight.unit);
  const [showKeyboard, setShowKeyboard] = useState(false);

  const dialogTitle =
    title ?? (isSoldByWeight ? 'Tabs.adjustWeight' : 'Tabs.adjustQuantity');

  const weightKg = useMemo(
    () => (isSoldByWeight ? parseWeightInput(quantity, unit) : null),
    [isSoldByWeight, quantity, unit]
  );

  const parsedUnitQuantity = Math.min(
    maxQuantity,
    Math.max(minQuantity, parseInt(quantity, 10) || minQuantity)
  );

  const parsedValue = isSoldByWeight ? (weightKg ?? NaN) : parsedUnitQuantity;

  const isWeightValid =
    weightKg != null &&
    weightKg <= maxQuantity &&
    weightKg >= minQuantity;

  const canConfirm = isSoldByWeight
    ? isWeightValid && !isConfirmDisabled
    : !isConfirmDisabled;

  const handleIncrement = () => {
    setQuantity((prev) => Math.min(maxQuantity, (parseInt(prev, 10) || 0) + 1).toString());
  };

  const handleDecrement = () => {
    setQuantity((prev) => Math.max(minQuantity, (parseInt(prev, 10) || 0) - 1).toString());
  };

  const handleSave = () => {
    if (!canConfirm || Number.isNaN(parsedValue)) return;
    onConfirm(parsedValue);
    onClose();
  };

  const handleCancel = () => {
    onClose();
    setShowKeyboard(false);
  };

  const handleUnitInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setQuantity(value);
    }
  };

  const handleWeightInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
      setQuantity(value);
    }
  };

  const handleKeyboardChange = (value: string) => {
    if (isSoldByWeight) {
      setQuantity(value);
      return;
    }
    if (value === '' || /^\d+$/.test(value)) {
      setQuantity(value);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent
        {...(isTouch ? { onOpenAutoFocus: (e) => e.preventDefault() } : {})}
        className={cn('sm:max-w-sm', isMobile ? 'w-80' : '')}
      >
        <DialogHeader>
          <DialogTitle>{translator(dialogTitle)}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground text-center sm:text-left">
            <span className="font-medium">{productName}</span>
            {isSoldByWeight && (
              <p className="mt-1 text-xs">
                {translator('Tabs.currentWeight')}: {formatWeight(currentQuantity)}
              </p>
            )}
          </div>

          {children}

          {isSoldByWeight ? (
            <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
              <div className="space-y-2">
                <Label htmlFor="tab-item-weight-input">
                  {translator('ProductsCatalog.weightLabel')}
                </Label>
                <Input
                  id="tab-item-weight-input"
                  type="text"
                  inputMode={useCustomNumpad ? 'none' : 'decimal'}
                  readOnly={useCustomNumpad}
                  value={quantity}
                  onChange={handleWeightInputChange}
                  onClick={() => useCustomNumpad && setShowKeyboard(true)}
                  placeholder={unit === 'g' ? '500' : '0.5'}
                  className="text-center text-2xl font-semibold h-12"
                  autoFocus={!isMobile && !isTouch}
                />
              </div>
              <Select value={unit} onValueChange={(v) => setUnit(v as WeightInputUnit)}>
                <SelectTrigger className="w-[88px] h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="g">{translator('ProductsCatalog.weightUnitGrams')}</SelectItem>
                  <SelectItem value="kg">{translator('ProductsCatalog.weightUnitKg')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3">
              <Button
                size="lg"
                variant="outline"
                onClick={handleDecrement}
                disabled={parsedUnitQuantity <= minQuantity}
                className="h-12 w-12 p-0"
              >
                <Minus className="h-5 w-5" />
              </Button>

              <Input
                type="text"
                inputMode={useCustomNumpad ? 'none' : 'numeric'}
                readOnly={useCustomNumpad}
                value={quantity}
                onChange={handleUnitInputChange}
                onClick={() => useCustomNumpad && setShowKeyboard(true)}
                className="text-center text-2xl font-semibold h-12 w-24"
                autoFocus={!isMobile && !isTouch}
              />

              <Button
                size="lg"
                variant="outline"
                onClick={handleIncrement}
                disabled={parsedUnitQuantity >= maxQuantity}
                className="h-12 w-12 p-0"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          )}

          {isSoldByWeight && quantity.trim() !== '' && !isWeightValid && (
            <p className="text-sm text-destructive text-center">
              {translator('ProductsCatalog.weightInvalid')}
            </p>
          )}

          {isSoldByWeight && isWeightValid && weightKg != null && weightKg > 0 && (
            <p className="text-xs text-muted-foreground text-center">
              {formatWeight(weightKg)}
            </p>
          )}

          <Button onClick={handleSave} disabled={!canConfirm} className="w-full">
            {translator(confirmLabel)}
          </Button>
        </div>
      </DialogContent>
      {useCustomNumpad && showKeyboard && (
        <SheetPortal>
          <div className="fixed inset-0 z-50 pointer-events-none">
            <div className="absolute bottom-20 right-0 w-80 h-auto max-h-[60vh] p-0 flex flex-col border-t border-l rounded-tl-lg bg-background shadow-lg pointer-events-auto">
              <NumericKeyboard
                value={quantity}
                onChange={handleKeyboardChange}
                onConfirm={handleSave}
                onCancel={() => setShowKeyboard(false)}
                translator={translator}
                title={
                  isSoldByWeight
                    ? translator('ProductsCatalog.weightLabel')
                    : undefined
                }
              />
            </div>
          </div>
        </SheetPortal>
      )}
    </Dialog>
  );
};

export default TabItemQuantityDialog;
